import React, { useState } from "react";

/**
 * ContactSection
 * Premium, fully responsive contact component for International Institute of Automate.
 * - Left: Address / company info card
 * - Right: Enquiry form (name, phone, email, message) -> redirects to WhatsApp with details
 * - Below: Location section with Hyderabad / Jaipur tabs switching an embedded map
 *
 * Drop this single file into a React project. Plain CSS is scoped via a
 * <style> tag inside the component so no external CSS file is required.
 */

const WHATSAPP_NUMBER = "8096120173"; // country code + number, no + or spaces

const LOCATIONS = {
  hyderabad: {
    label: "Hyderabad",
    name: "Manjeera Trinity Corporate",
    address:
      "Manjeera Trinity Corporate, 6th Floor, Tower B, Kukatpally, Hyderabad, Telangana 500072",
    mapQuery:
      "Manjeera Trinity Corporate, 6th Floor, Tower B, Kukatpally, Hyderabad, Telangana 500072",
  },
  jaipur: {
    label: "Jaipur",
    name: "Regus Jaipur",
    address:
      "Nr 205, Second Floor, Regus Jaipur, Plot no: B/C 59 Central Spine, OB Infra Tower, Jaipur, 302025, Rajasthan",
    mapQuery:
      "OB Infra Tower, Central Spine, Vidhyadhar Nagar, Jaipur, Rajasthan 302025",
  },
};

const COMPANY = {
  phone: "+91 8096120173",
  phoneHref: "tel:+918096120173",
  email: "info@iiautomate.com",
};

export default function ContactSection() {
  const [activeCity, setActiveCity] = useState("hyderabad");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!form.phone.trim()) {
      next.phone = "Please enter your phone number";
    } else if (!/^[0-9+\-\s]{7,15}$/.test(form.phone.trim())) {
      next.phone = "Enter a valid phone number";
    }
    if (!form.email.trim()) {
      next.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = "Enter a valid email address";
    }
    if (!form.message.trim()) next.message = "Please enter your message";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const text =
      `New Enquiry - International Institute of Automate%0A%0A` +
      `*Name:* ${encodeURIComponent(form.name)}%0A` +
      `*Phone:* ${encodeURIComponent(form.phone)}%0A` +
      `*Email:* ${encodeURIComponent(form.email)}%0A` +
      `*Message:* ${encodeURIComponent(form.message)}`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;

    setSubmitted(true);
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");

    setTimeout(() => {
      setForm({ name: "", phone: "", email: "", message: "" });
      setSubmitted(false);
    }, 2500);
  };

  const current = LOCATIONS[activeCity];
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
    current.mapQuery
  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  return (
    <section className="ct-section">
      <style>{`
        .ct-section {
          --navy: #0b1f3a;
          --navy-deep: #081729;
          --amber: #f0a63c;
          --amber-deep: #d9891f;
          --ink: #17233b;
          --muted: #64748b;
          --line: #e4e8f0;
          --bg-soft: #f6f8fb;
          --white: #ffffff;
          --radius-lg: 20px;
          --radius-md: 14px;
          --shadow-soft: 0 20px 45px -25px rgba(11, 31, 58, 0.35);
          font-family: "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
          background: var(--bg-soft);
          padding: 104px 20px;
          box-sizing: border-box;
        }
        .ct-section *, .ct-section *::before, .ct-section *::after {
          box-sizing: border-box;
        }

        .ct-container {
          max-width: 1140px;
          margin: 0 auto;
        }

        .ct-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--amber-deep);
          margin-bottom: 10px;
        }
        .ct-eyebrow::before {
          content: "";
          width: 22px;
          height: 2px;
          background: var(--amber);
          display: inline-block;
        }
        .ct-heading {
          font-size: clamp(28px, 4vw, 38px);
          font-weight: 800;
          color: var(--navy);
          margin: 0 0 12px;
          letter-spacing: -0.01em;
        }
        .ct-subheading {
          font-size: 15.5px;
          color: var(--muted);
          max-width: 560px;
          line-height: 1.6;
          margin: 0 0 44px;
        }

        /* ---------- Top grid: address + enquiry ---------- */
        .ct-grid {
          display: grid;
          grid-template-columns: 0.85fr 1.15fr;
          gap: 28px;
          align-items: stretch;
        }

        .ct-address-card {
          background: blue;
          border-radius: var(--radius-lg);
          padding: 40px 32px;
          color: #fff;
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-soft);
        }
        .ct-address-card::after {
          content: "";
          position: absolute;
          width: 220px;
          height: 220px;
          right: -70px;
          bottom: -90px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(240, 166, 60, 0.28), transparent 70%);
        }
        .ct-address-card h3 {
          font-size: 21px;
          font-weight: 700;
          margin: 0 0 6px;
        }
        .ct-address-card p.ct-lead {
          color: white;
          font-size: 13.5px;
          margin: 0 0 30px;
          line-height: 1.55;
        }

        .ct-info-row {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          padding: 16px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
          position: relative;
          z-index: 1;
        }
        .ct-info-row:last-of-type {
          border-bottom: 1px solid rgba(255, 255, 255, 0.12);
        }
        .ct-info-icon {
          flex: none;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: rgba(240, 166, 60, 0.16);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
        }
        .ct-info-text .ct-info-label {
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        
          margin-bottom: 4px;
          font-weight: 600;
        }
        .ct-info-text a,
        .ct-info-text span {
          color: #fff;
          font-size: 14.5px;
          line-height: 1.5;
          text-decoration: none;
          font-weight: 500;
        }
        .ct-info-text a:hover {
          color: var(--amber);
        }

        .ct-address-card .ct-cities {
          margin-top: auto;
          padding-top: 28px;
          display: flex;
          gap: 10px;
          position: relative;
          z-index: 1;
        }
        .ct-city-pill {
          font-size: 12px;
          font-weight: 600;
          padding: 7px 14px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.16);
          color: rgba(255, 255, 255, 0.85);
        }

        /* ---------- Enquiry form ---------- */
        .ct-form-card {
          background: var(--white);
          border-radius: var(--radius-lg);
          padding: 40px 36px;
          box-shadow: var(--shadow-soft);
          border: 1px solid var(--line);
        }
        .ct-form-card h3 {
          font-size: 21px;
          font-weight: 700;
          color: var(--navy);
          margin: 0 0 6px;
        }
        .ct-form-card p.ct-lead {
          color: var(--muted);
          font-size: 13.5px;
          margin: 0 0 26px;
        }

        .ct-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }
        .ct-field {
          margin-bottom: 18px;
        }
        .ct-field label {
          display: block;
          font-size: 12.5px;
          font-weight: 600;
          color: var(--ink);
          margin-bottom: 7px;
        }
        .ct-field input,
        .ct-field textarea {
          width: 100%;
          border: 1.5px solid var(--line);
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14.5px;
          font-family: inherit;
          color: var(--ink);
          background: var(--bg-soft);
          transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
        }
        .ct-field input::placeholder,
        .ct-field textarea::placeholder {
          color: #9aa4b5;
        }
        .ct-field input:focus,
        .ct-field textarea:focus {
          outline: none;
          border-color: var(--amber);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(240, 166, 60, 0.15);
        }
        .ct-field textarea {
          resize: vertical;
          min-height: 110px;
        }
        .ct-field.has-error input,
        .ct-field.has-error textarea {
          border-color: #e0554f;
          background: #fff6f5;
        }
        .ct-error-text {
          color: #d3413a;
          font-size: 12px;
          margin-top: 6px;
          font-weight: 500;
        }

        .ct-submit-btn {
          width: 100%;
          border: none;
          border-radius: 10px;
          padding: 14px 20px;
          background: blue;
          color: white;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          transition: transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease;
          box-shadow: 0 14px 26px -12px rgba(217, 137, 31, 0.55);
        }
        .ct-submit-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 18px 30px -12px rgba(217, 137, 31, 0.6);
        }
        .ct-submit-btn:active {
          transform: translateY(0);
        }
        .ct-submit-btn.is-sent {
          background: #1fa25b;
          color: #fff;
          box-shadow: none;
        }
        .ct-wa-icon {
          width: 18px;
          height: 18px;
          flex: none;
        }
        .ct-form-note {
          text-align: center;
          font-size: 12px;
          color: var(--muted);
          margin-top: 12px;
        }

        /* ---------- Location section ---------- */
        .ct-loc-section {
          margin-top: 64px;
        }
        .ct-loc-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 26px;
        }
        .ct-tabs {
          display: inline-flex;
          background: #fff;
          border: 1px solid var(--line);
          border-radius: 999px;
          padding: 5px;
          box-shadow: 0 6px 18px -12px rgba(11, 31, 58, 0.25);
        }
        .ct-tab-btn {
          border: none;
          background: transparent;
          padding: 10px 22px;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--muted);
          border-radius: 999px;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ct-tab-btn.active {
          background: var(--navy);
          color: #fff;
        }

        .ct-loc-body {
          display: grid;
          grid-template-columns: 1fr 1.35fr;
          gap: 0;
          border-radius: var(--radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-soft);
          border: 1px solid var(--line);
        }
        .ct-loc-details {
          background: #fff;
          padding: 34px 32px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .ct-loc-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--amber-deep);
          background: rgba(240, 166, 60, 0.12);
          padding: 6px 12px;
          border-radius: 999px;
          margin-bottom: 16px;
          width: fit-content;
        }
        .ct-loc-details h4 {
          font-size: 19px;
          font-weight: 700;
          color: var(--navy);
          margin: 0 0 10px;
        }
        .ct-loc-details p {
          font-size: 14px;
          color: var(--muted);
          line-height: 1.65;
          margin: 0 0 22px;
        }
        .ct-loc-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .ct-loc-actions a {
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          padding: 10px 16px;
          border-radius: 9px;
          border: 1.5px solid var(--line);
          color: var(--navy);
          transition: border-color 0.15s ease, color 0.15s ease;
        }
        .ct-loc-actions a:hover {
          border-color: var(--amber);
          color: var(--amber-deep);
        }

        .ct-map-wrap {
          min-height: 320px;
          background: #dfe4ec;
        }
        .ct-map-wrap iframe {
          width: 100%;
          height: 100%;
          min-height: 320px;
          border: 0;
          display: block;
        }

        /* ---------- Responsive ---------- */
        @media (max-width: 900px) {
          .ct-grid {
            grid-template-columns: 1fr;
          }
          .ct-loc-body {
            grid-template-columns: 1fr;
          }
          .ct-map-wrap {
            min-height: 260px;
          }
          .ct-map-wrap iframe {
            min-height: 260px;
          }
        }

        @media (max-width: 560px) {
          .ct-section {
            padding: 48px 14px;
          }
          .ct-address-card,
          .ct-form-card {
            padding: 28px 20px;
          }
          .ct-form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }
          .ct-loc-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .ct-tabs {
            width: 100%;
          }
          .ct-tab-btn {
            flex: 1;
            padding: 10px 12px;
          }
          .ct-loc-details {
            padding: 26px 20px;
          }
        }
      `}</style>

      <div className="ct-container">
        <span className="ct-eyebrow">Get in touch</span>
        <h2 className="ct-heading">Contact International Institute of Automate</h2>
        <p className="ct-subheading">
          Have a question about our programs, corporate training, or academic
          partnerships? Reach out below and our team will get back to you on
          WhatsApp right away.
        </p>

        <div className="ct-grid">
          {/* Address box */}
          <div className="ct-address-card">
            <h3>Our details</h3>
            <p className="ct-lead">
              Reach us directly by phone or email, or drop by one of our
              centers.
            </p>

            <div className="ct-info-row">
              <span className="ct-info-icon">📞</span>
              <div className="ct-info-text">
                <div className="ct-info-label">Phone</div>
                <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>
              </div>
            </div>

            <div className="ct-info-row">
              <span className="ct-info-icon">✉️</span>
              <div className="ct-info-text">
                <div className="ct-info-label">Email</div>
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              </div>
            </div>

            <div className="ct-info-row">
              <span className="ct-info-icon">📍</span>
              <div className="ct-info-text">
                <div className="ct-info-label">
                  {LOCATIONS[activeCity].label} Center
                </div>
                <span>{LOCATIONS[activeCity].address}</span>
              </div>
            </div>

            <div className="ct-cities">
              <span className="ct-city-pill">Hyderabad</span>
              <span className="ct-city-pill">Jaipur</span>
            </div>
          </div>

          {/* Enquiry box */}
          <div className="ct-form-card">
            <h3>Send an enquiry</h3>
            <p className="ct-lead">
              Fill in your details — this opens WhatsApp with your message
              ready to send.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="ct-form-row">
                <div className={`ct-field ${errors.name ? "has-error" : ""}`}>
                  <label htmlFor="ct-name">Full name</label>
                  <input
                    id="ct-name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                  />
                  {errors.name && (
                    <div className="ct-error-text">{errors.name}</div>
                  )}
                </div>

                <div className={`ct-field ${errors.phone ? "has-error" : ""}`}>
                  <label htmlFor="ct-phone">Phone number</label>
                  <input
                    id="ct-phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && (
                    <div className="ct-error-text">{errors.phone}</div>
                  )}
                </div>
              </div>

              <div className={`ct-field ${errors.email ? "has-error" : ""}`}>
                <label htmlFor="ct-email">Email address</label>
                <input
                  id="ct-email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <div className="ct-error-text">{errors.email}</div>
                )}
              </div>

              <div className={`ct-field ${errors.message ? "has-error" : ""}`}>
                <label htmlFor="ct-message">Message</label>
                <textarea
                  id="ct-message"
                  name="message"
                  placeholder="Tell us what you're looking for..."
                  value={form.message}
                  onChange={handleChange}
                />
                {errors.message && (
                  <div className="ct-error-text">{errors.message}</div>
                )}
              </div>

              <button
                type="submit"
                className={`ct-submit-btn ${submitted ? "is-sent" : ""}`}
              >
                {submitted ? (
                  "Opening WhatsApp..."
                ) : (
                  <>
                    
                    Send Enquiry
                  </>
                )}
              </button>
              <div className="ct-form-note">
                We reply on WhatsApp, usually within a few hours.
              </div>
            </form>
          </div>
        </div>

        {/* Location section */}
        <div className="ct-loc-section">
          <div className="ct-loc-header">
            <div>
              <span className="ct-eyebrow">Visit us</span>
              <h2 className="ct-heading" style={{ marginBottom: 0 }}>
                Our centers
              </h2>
            </div>

            <div className="ct-tabs" role="tablist" aria-label="Choose location">
              {Object.entries(LOCATIONS).map(([key, loc]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={activeCity === key}
                  className={`ct-tab-btn ${activeCity === key ? "active" : ""}`}
                  onClick={() => setActiveCity(key)}
                  type="button"
                >
                  {loc.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ct-loc-body">
            <div className="ct-loc-details">
              <span className="ct-loc-badge">📍 {current.label}</span>
              <h4>{current.name}</h4>
              <p>{current.address}</p>
              <div className="ct-loc-actions">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    current.mapQuery
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get directions
                </a>
                <a href={COMPANY.phoneHref}>Call us</a>
              </div>
            </div>

            <div className="ct-map-wrap">
              <iframe
                key={activeCity}
                src={mapSrc}
                title={`${current.label} location map`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}